/*
 * Перехватчик ошибок редактора.
 *
 * ЗАЧЕМ. Без него любая ошибка рендера роняет ВСЁ дерево в пустоту: React
 * размонтирует приложение, и на экране остаётся только заставка «Загружаю
 * редактор…» из index.html. Именно так выглядели падения при работе с эскизом —
 * человек видел вечную загрузку и ничего больше, а в консоли лежал
 * минифицированный стек без единого имени компонента.
 *
 * Теперь: показываем понятный экран, ГДЕ именно сломалось (React отдаёт стек
 * компонентов в componentStack), и даём вернуться к проектам не перезагружая
 * вкладку. Ошибка при этом остаётся в консоли и уходит в крашлог.
 */
import React from 'react';
import { reportCrash } from './crashReporter';

/* Метка сборки. Меняется вручную при каждом выкате — по ней сразу видно,
   какой код выполняется в браузере на самом деле. */
export const BUILD_TAG = 'F15-фикс-удаления-тел-10.08';

export default class CadErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, stack: '' };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        const stack = (info && info.componentStack) || '';
        // В консоль — полностью, чтобы можно было прислать скриншот.
        console.error('[CadErrorBoundary]', error, '\nКОМПОНЕНТЫ:', stack);
        this.setState({ stack });
        try {
            reportCrash({
                level: 'error',
                category: 'react',
                message: `UI: ${error?.message || error}`,
                stack: stack.slice(0, 1500),
            });
        } catch (e) { /* краш-репортер не должен ронять сам себя */ }
    }

    render() {
        if (!this.state.error) return this.props.children;

        // Первые строки стека компонентов — это и есть виновник.
        const top = this.state.stack
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean)
            .slice(0, 6)
            .join('\n');

        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', height: '100%', gap: 14, padding: 24,
                background: 'var(--k-bg, #14161c)', color: 'var(--k-ink, #eef1f6)',
                fontFamily: 'var(--k-font, system-ui)', textAlign: 'center',
            }}>
                <h2 style={{ margin: 0, fontSize: 22 }}>Редактор споткнулся</h2>
                {/* Версия сборки прямо на экране: без неё невозможно понять,
                    какой JS реально выполняется — браузер может держать старый
                    бандл в памяти вкладки, и стек ошибки ссылается на файл,
                    которого на сервере уже нет. */}
                <div style={{
                    fontSize: 12, letterSpacing: '.04em', textTransform: 'uppercase',
                    color: 'var(--k-ink-mute, #8b93a5)',
                }}>
                    сборка {BUILD_TAG}
                </div>
                <p style={{ margin: 0, color: 'var(--k-ink-soft, #b9c0cf)', maxWidth: 520, lineHeight: 1.5 }}>
                    {String(this.state.error?.message || this.state.error)}
                </p>
                {top && (
                    <pre style={{
                        margin: 0, padding: '12px 14px', maxWidth: 640, overflow: 'auto',
                        background: 'var(--k-surface-2, #21252f)',
                        border: '1px solid var(--k-line, #313745)',
                        borderRadius: 12, fontSize: 12, lineHeight: 1.5,
                        color: 'var(--k-ink-mute, #8b93a5)', textAlign: 'left',
                    }}>{top}</pre>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                    <button
                        onClick={() => this.setState({ error: null, stack: '' })}
                        style={btn('var(--k-accent, #38d9f0)', 'var(--k-accent-ink, #06222a)')}
                    >
                        Попробовать снова
                    </button>
                    <a href="/main/cad-editor" style={{ ...btn('transparent', 'var(--k-ink-soft, #b9c0cf)'), border: '1px solid var(--k-line, #313745)', textDecoration: 'none' }}>
                        К проектам
                    </a>
                </div>
            </div>
        );
    }
}

function btn(bg, color) {
    return {
        padding: '11px 24px', borderRadius: 12, border: 'none',
        background: bg, color, fontSize: 15, fontWeight: 700,
        cursor: 'pointer', fontFamily: 'inherit',
    };
}
